import * as React from "react"
import { cn } from "@/lib/utils"
import { ProductImage } from "./ui/ProductImage"
import { Input } from "./ui/input"
import { Button } from "./ui/button"
import { CheckmarkIcon, ArrowRightIcon, HelpIcon } from "./icons"
import { PendingRequestBanner } from "./PendingRequestBanner"

export interface PairingItemFormProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Product title (e.g., "NFC XP")
   */
  productTitle: string
  /**
   * Product image URL
   */
  productImage: string
  /**
   * NFC ID to display (read-only)
   */
  nfcId: string
  /**
   * Whether the NFC ID is verified
   * @default false
   */
  nfcVerified?: boolean
  /**
   * First word of the pairing code
   */
  codeFirst?: string
  /**
   * Second word of the pairing code
   */
  codeSecond?: string
  /**
   * Callback when first code word changes
   */
  onCodeFirstChange?: (value: string) => void
  /**
   * Callback when second code word changes
   */
  onCodeSecondChange?: (value: string) => void
  /**
   * Callback when validate & pair button is clicked
   */
  onValidate?: () => void
  /**
   * Whether the form is in loading state
   * @default false
   */
  loading?: boolean
  /**
   * Whether the validate button is disabled
   * @default false
   */
  disabled?: boolean
  /**
   * Error message when code validation fails (global error for both inputs)
   */
  error?: string
  /**
   * Whether the code inputs are required
   * @default true
   */
  codeRequired?: boolean
  /**
   * Whether to show the pending request banner
   * @default false
   */
  showPendingBanner?: boolean
  /**
   * ISO 8601 timestamp when pending request expires
   */
  pendingRequestExpiresAt?: string
  /**
   * Whether to show competition warning (others are also pairing)
   * @default false
   */
  showCompetitionWarning?: boolean
}

/**
 * Pairing Item Form component for NFC product pairing
 *
 * This component displays a product image, NFC ID, and optional two-word code inputs
 * for pairing physical NFC products with the user's Trophy Room.
 *
 * @example
 * ```tsx
 * <PairingItemForm
 *   productTitle="NFC XP"
 *   productImage="/images/jacket.jpg"
 *   nfcId="adnoa46z5vr1z35ef"
 *   nfcVerified={true}
 *   codeFirst={firstWord}
 *   codeSecond={secondWord}
 *   onCodeFirstChange={setFirstWord}
 *   onCodeSecondChange={setSecondWord}
 *   onValidate={handlePairing}
 * />
 * ```
 */
export const PairingItemForm = React.forwardRef<HTMLDivElement, PairingItemFormProps>(
  (
    {
      productTitle,
      productImage,
      nfcId,
      nfcVerified = false,
      codeFirst = "",
      codeSecond = "",
      onCodeFirstChange,
      onCodeSecondChange,
      onValidate,
      loading = false,
      disabled = false,
      error,
      codeRequired = true,
      showPendingBanner = false,
      pendingRequestExpiresAt,
      showCompetitionWarning = false,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          "flex flex-col gap-8 items-center",
          className
        )}
        {...props}
      >
        {/* Title */}
        <div className="flex gap-4 items-center w-full">
          <p className="font-montserrat font-bold text-[0.9375rem] text-gray-900 text-center uppercase">
            Pairing {productTitle}
          </p>
        </div>

        {/* Pending Request Banner */}
        {showPendingBanner && pendingRequestExpiresAt && (
          <PendingRequestBanner expiresAt={pendingRequestExpiresAt} className="w-full" />
        )}

        {/* Competition Warning Banner */}
        {showCompetitionWarning && (
          <div className="w-full p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-[0.3125rem]">
            <p className="text-[0.8125rem] text-yellow-800 dark:text-yellow-200 font-montserrat font-normal">
              ⚠️ Others are also attempting to pair this item. First to verify wins.
            </p>
          </div>
        )}

        {/* Product Image */}
        <div className="relative h-[12.6875rem] w-[11.9375rem] shrink-0">
          <ProductImage
            src={productImage}
            alt={productTitle}
            containerClassName="absolute inset-0"
          />
        </div>

        {/* NFC ID Display */}
        <div className="bg-white border border-gray-100 border-solid flex h-12 items-center justify-between px-4 rounded-[0.3125rem] w-full">
          <div className="flex flex-col gap-1 items-start">
            <div className="flex flex-col justify-end text-xs">
              <p className="font-montserrat font-normal leading-5 text-gray-500">
                NFC ID :
              </p>
            </div>
            <div className="flex flex-col justify-end text-[1.125rem] tracking-[0.25rem]">
              <p className="font-montserrat font-normal leading-5 text-gray-500">
                {nfcId}
              </p>
            </div>
          </div>
          {nfcVerified && (
            <div className="flex gap-4 items-start px-0 py-2">
              <CheckmarkIcon
                size={12}
                weight="bold"
                className="text-brand-green"
              />
            </div>
          )}
        </div>

        {/* Secret Code Section */}
        <div className="flex flex-col gap-4 items-start w-full">
          {/* Label with help icon */}
          <div className="flex items-center justify-between w-full">
            <div className="flex flex-col justify-end">
              <p className="font-montserrat font-normal text-[0.9375rem] leading-5 text-gray-500">
                Please fill your secret code here
              </p>
            </div>
            <button
              type="button"
              className="flex flex-col justify-center size-[0.9375rem] cursor-pointer"
              aria-label="Help with secret code"
            >
              <HelpIcon size={16} className="text-gray-500" />
            </button>
          </div>

          {/* Code inputs with error messages */}
          <div className="flex gap-4 items-start w-full flex-col">
            {error && (
              <p className="text-[0.8125rem] text-brand-red font-montserrat font-normal w-full italic">
                {error}
              </p>
            )}
            <div className="flex gap-4 items-start w-full">
              <Input
                variant="anyw3ar"
                placeholder="First"
                value={codeFirst}
                onChange={(e) => onCodeFirstChange?.(e.target.value)}
                disabled={loading}
                className={cn(
                  error && "border-brand-red text-brand-red placeholder:text-brand-red"
                )}
              />
              <Input
                variant="anyw3ar"
                placeholder="Second"
                value={codeSecond}
                onChange={(e) => onCodeSecondChange?.(e.target.value)}
                disabled={loading}
                className={cn(
                  error && "border-brand-red text-brand-red placeholder:text-brand-red"
                )}
              />
            </div>

          </div>
        </div>

        {/* Validate & Pair Button */}
        <Button
          variant="primary-dark"
          size="primary-dark"
          onClick={onValidate}
          disabled={disabled || loading}
          leftIcon={<ArrowRightIcon size={12} />}
          className="w-full h-10 rounded-[0.375rem] uppercase"
        >
          {loading ? "Pairing..." : "Validate & PAIR"}
        </Button>

      </div>
    )
  }
)

PairingItemForm.displayName = "PairingItemForm"
